import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { MEMBERS_URL, meQuery, signOut } from '~/lib/adb/members'
import type { Member } from '~/lib/adb/members'
import { Button } from '~/components/ui/Button'
import { Container } from '~/components/ui/Container'
import { FormStatus } from '~/components/ui/Field'
import { PageHero } from '~/components/ui/PageHero'
import { PageTitle } from '~/components/ui/PageTitle'

export const Route = createFileRoute('/account')({
  headers: () => cacheHeaders.content,
  head: () =>
    pageMeta({
      title: 'Your account',
      description: 'Sign in to your Direct Action Everywhere account.',
      noindex: true,
    }),
  component: Account,
})

function Account() {
  // Only runs in the browser (the members host is another origin), so the
  // prerendered page and the first client render both show "Loading...".
  // `member` is null when signed out; the query errors if the host is down.
  const { data: member, isPending, isError } = useQuery(meQuery())

  return (
    <PageHero as="div" bottom="page">
      <Container width="form">
        <PageTitle className="pb-5 text-center">Your account</PageTitle>
        {isPending ? (
          <p className="text-center text-fg-muted">Loading...</p>
        ) : isError && member === undefined ? (
          <FormStatus kind="error">
            We couldn&apos;t reach the sign-in service right now. Please try
            again in a few minutes.
          </FormStatus>
        ) : !member ? (
          <SignIn />
        ) : (
          <SignedIn member={member} />
        )}
      </Container>
    </PageHero>
  )
}

function SignIn() {
  // location isn't available at prerender; this only renders after hydration.
  const next = encodeURIComponent(location.href)
  return (
    <div className="text-center">
      <p className="text-lg leading-[26px]">Sign in to see your DxE account.</p>
      <div className="pt-8">
        <Button href={`${MEMBERS_URL}/login?next=${next}`}>Sign in</Button>
      </div>
    </div>
  )
}

function SignedIn({ member }: { member: Member }) {
  const queryClient = useQueryClient()
  const signOutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: () => queryClient.setQueryData(meQuery().queryKey, null),
    onError: (err) => console.error(err),
  })

  return (
    <div className="text-center">
      <p className="text-lg leading-[26px]">
        Signed in as <strong className="break-words">{member.email}</strong>
      </p>
      {member.name && (
        <p className="pt-1 text-lg leading-[26px]">{member.name}</p>
      )}
      {signOutMutation.isError && (
        <FormStatus kind="error" className="mt-5">
          Couldn&apos;t sign out. Please try again.
        </FormStatus>
      )}
      <div className="pt-8">
        <Button
          variant="outline"
          disabled={signOutMutation.isPending}
          onClick={() => signOutMutation.mutate()}
        >
          Sign out
        </Button>
      </div>
    </div>
  )
}
